import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  DateRange as DateIcon,
  Euro as EuroIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

const PendingOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(false);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);

  useEffect(() => {
    loadPendingOrders();
  }, []);

  const loadPendingOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getPendingOrders({ per_page: 50 });
      setOrders(response.data || []);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get unique statuses from current orders
  const getAvailableStatuses = () => {
    const uniqueStatuses = [...new Set(orders.map(order => order.status))];
    return uniqueStatuses.sort();
  };

  // Filter orders based on selected status filters
  const getFilteredOrders = () => {
    if (selectedStatusFilters.length === 0) {
      return orders;
    }
    return orders.filter(order => selectedStatusFilters.includes(order.status));
  };

  // Handle status filter toggle
  const handleStatusFilterToggle = (status) => {
    setSelectedStatusFilters(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedStatusFilters([]);
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'processing': 'info',
      'on-hold': 'error',
      'completed': 'success',
      'cancelled': 'default',
      'refunded': 'error',
      'failed': 'error',
      // Custom hiphot.nl statussen
      'intern-magazijn': 'info',
      'logo-ok': 'success',
      'logo-print': 'warning',
      'logo-proofprint': 'warning',
      'logo-received': 'info',
      'design-request': 'warning',
      'logo-request': 'warning'
    };
    return colors[status] || 'default';
  };

  const getOrderStatusLabel = (status) => {
    const labels = {
      'pending': 'In afwachting',
      'processing': 'In behandeling',
      'on-hold': 'Wacht',
      'completed': 'Afgerond',
      'cancelled': 'Geannuleerd',
      'refunded': 'Terugbetaald',
      'failed': 'Mislukt',
      // Custom hiphot.nl statussen
      'intern-magazijn': 'Magazijn HH',
      'logo-ok': 'Logo akkoord',
      'logo-print': 'Logo naar drukker',
      'logo-proofprint': 'Logo proefdruk',
      'logo-received': 'Logo ontvangen',
      'design-request': 'Design opvragen',
      'logo-request': 'Logo opvragen'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAccordionChange = (orderId) => (event, isExpanded) => {
    setExpandedOrder(isExpanded ? orderId : false);
  };

  const OrderCard = ({ order }) => {
    // Helper function to get customer display name - ALWAYS use company name
    const getCustomerDisplayName = (billing) => {
      // Always use company name if available, otherwise use full name
      if (billing?.company) {
        return billing.company;
      }
      const fullName = `${billing?.first_name || ''} ${billing?.last_name || ''}`.trim();
      if (fullName) {
        return fullName;
      }
      return 'Onbekende klant';
    };

    const customerName = getCustomerDisplayName(order.billing);
    const customerEmail = order.billing?.email || 'Geen email';
    
    return (
      <Accordion 
        expanded={expandedOrder === order.id} 
        onChange={handleAccordionChange(order.id)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" color="primary">
                Order #{order.number}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ID: {order.id}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  {customerName || 'Onbekende klant'}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {customerEmail}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <DateIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {formatDate(order.date_created)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <EuroIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  €{parseFloat(order.total).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Chip 
                label={getOrderStatusLabel(order.status)}
                color={getOrderStatusColor(order.status)}
                size="small"
              />
            </Grid>
          </Grid>
        </AccordionSummary>
        
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Customer Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Klantgegevens
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2"><strong>Naam:</strong> {customerName}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {customerEmail}</Typography>
                <Typography variant="body2"><strong>Telefoon:</strong> {order.billing?.phone || 'Niet opgegeven'}</Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2"><strong>Factuuradres:</strong></Typography>
                <Typography variant="body2">
                  {order.billing?.address_1} {order.billing?.address_2}<br />
                  {order.billing?.postcode} {order.billing?.city}<br />
                  {order.billing?.country}
                </Typography>
                
                {order.shipping && order.shipping.address_1 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2"><strong>Verzendadres:</strong></Typography>
                    <Typography variant="body2">
                      {order.shipping.address_1} {order.shipping.address_2}<br />
                      {order.shipping.postcode} {order.shipping.city}<br />
                      {order.shipping.country}
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
            
            {/* Order Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Order Informatie
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2"><strong>Order nummer:</strong> #{order.number}</Typography>
                <Typography variant="body2"><strong>Order datum:</strong> {formatDate(order.date_created)}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {getOrderStatusLabel(order.status)}</Typography>
                <Typography variant="body2"><strong>Betaalmethode:</strong> {order.payment_method_title || 'Niet opgegeven'}</Typography>
                <Typography variant="body2"><strong>Verzendmethode:</strong> {order.shipping_lines?.[0]?.method_title || 'Niet opgegeven'}</Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2"><strong>Subtotaal:</strong> €{parseFloat(order.total - order.total_tax - order.shipping_total).toFixed(2)}</Typography>
                <Typography variant="body2"><strong>Verzendkosten:</strong> €{parseFloat(order.shipping_total).toFixed(2)}</Typography>
                <Typography variant="body2"><strong>BTW:</strong> €{parseFloat(order.total_tax).toFixed(2)}</Typography>
                <Typography variant="body1"><strong>Totaal:</strong> €{parseFloat(order.total).toFixed(2)}</Typography>
              </Paper>
            </Grid>
            
            {/* Order Items */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Bestelde Producten
              </Typography>
              <Paper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="center">Aantal</TableCell>
                      <TableCell align="right">Prijs</TableCell>
                      <TableCell align="right">Totaal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.line_items?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {item.name}
                          </Typography>
                          {item.meta_data && item.meta_data.length > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              {item.meta_data.map(meta => {
                                // Special handling for COG fields
                                if (meta.key === '_alg_wc_cog_item_cost') {
                                  return `Costprice: €${parseFloat(meta.value).toFixed(2)}`;
                                } else if (meta.key === '_alg_wc_cog_item_profit') {
                                  return `Profit: €${parseFloat(meta.value).toFixed(2)}`;
                                } else {
                                  return `${meta.display_key}: ${meta.display_value}`;
                                }
                              }).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.sku || 'Geen SKU'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {item.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            €{parseFloat(item.price).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            €{parseFloat(item.total).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Openstaande orders worden geladen...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={loadPendingOrders}>
            Opnieuw proberen
          </Button>
        }
      >
        Fout bij het laden van orders: {error}
      </Alert>
    );
  }

  const availableStatuses = getAvailableStatuses();
  const filteredOrders = getFilteredOrders();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Openstaande Orders
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadPendingOrders}
          disabled={loading}
        >
          Vernieuwen
        </Button>
      </Box>

      {/* Status Filter Chips */}
      {availableStatuses.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <FilterListIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Filter op Status
              </Typography>
              {selectedStatusFilters.length > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={clearAllFilters}
                  sx={{ ml: 'auto' }}
                >
                  Wis alle filters
                </Button>
              )}
            </Box>
            
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {availableStatuses.map((status) => {
                const isSelected = selectedStatusFilters.includes(status);
                const orderCount = orders.filter(order => order.status === status).length;
                
                return (
                  <Chip
                    key={status}
                    label={`${getOrderStatusLabel(status)} (${orderCount})`}
                    color={isSelected ? getOrderStatusColor(status) : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => handleStatusFilterToggle(status)}
                    sx={{ 
                      mb: 1,
                      '&:hover': {
                        backgroundColor: isSelected ? undefined : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  />
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {filteredOrders.length === 0 ? (
        <Box textAlign="center" py={8}>
          <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {selectedStatusFilters.length > 0 
              ? 'Geen orders gevonden met geselecteerde filters' 
              : 'Geen openstaande orders gevonden'
            }
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {selectedStatusFilters.length > 0 
              ? 'Probeer andere filters of wis alle filters' 
              : 'Alle orders zijn afgerond of geannuleerd'
            }
          </Typography>
        </Box>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            {filteredOrders.length} van {orders.length} orders {selectedStatusFilters.length > 0 ? 'gefilterd' : 'gevonden'}
            {selectedStatusFilters.length > 0 && (
              <> op status: {selectedStatusFilters.map(s => getOrderStatusLabel(s)).join(', ')}</>
            )}
          </Alert>

          {filteredOrders.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </>
      )}
    </Box>
  );
};

export default PendingOrders;
